import React, { useEffect, useState, useRef } from 'react';
import SearchFilter from './searchFilter';
import ContactListItem from './contactListItem';
import Button from 's2s-button';
import LazyLoader from 's2s-lazy-loader';
import styled from 'styled-components';
import { colorGrid } from 's2s-themes';
import PropTypes from 'prop-types';
import { getIntlText } from '../js/utilities';
import useContacts from "../hooks/contacts";
import { track } from 's2s-app-service';

const ZeroStateContainer = styled.div`
  align-items: center;
  background-color: ${colorGrid.gray1};
  display: flex;
  flex-direction: column;
  padding: 0 40px;
`;

const RetrievingStateContainer = styled.div`
  align-items: center;
  margin-top: 30vh;
  height: 100%;
  width: 100%;
  background-color: ${colorGrid.gray1};
  display: flex;
  flex-direction: column;
`;

const ZeroStateText = styled.div`
  color: #5e6366;
  font-family: Open Sans;
  font-size: 14px;
  text-align: center;
`;

const ZeroStateTextImport = styled.div`
  padding-top: 8px;
  font-size: 16px;
  color: ${colorGrid.gray5};
`;

const ZeroStateButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 18px;
  width: 100%;
`;

const ImportContactsButton = styled(Button)`
  background-color: #243991;
  font-family: Open Sans;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 1.25px;
  margin-bottom: 8px;
  text-transform: uppercase;
  
  :hover {
    background-color: #182661;
  };
`;

const AddContactButton = styled(Button)`
  background-color: #fff;
  border: 1px solid #a7b4e9;
  color: #243991;
  font-family: Open Sans;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  
  :hover {
    background-color: #d3d9f4;
  };
`;

const ContactGroupListContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 1em;
  max-height: 100%;
  position: relative;
`;

const MainGroupArea = styled.div`
  background-color: ${colorGrid.gray1};
  border-bottom: 1px solid #e5e6e8;
  border-top: 1px solid #e5e6e8;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: calc(100vh - 104px); // 104px = 48px (Header height) + 56px (SearchFilterContainer height); the height must be calculated here so that Lazyloader can function properly
  margin-bottom: 0px;
  margin-top: 0px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const StyledListItem = styled.li`
  background-color: ${colorGrid.gray0};
  box-shadow: inset 0 -1px 0 0 ${colorGrid.gray3};
  color: ${colorGrid.gray5}; //was '#929292'
  display: block;
  width: 100%;
`;

const StyledLiContent = styled.div`
  background-color: ${colorGrid.gray0};
  display: flex;
  flex: none;
  list-style-type: none;
  margin: 0px;
  padding: 0px;
  flex-direction: column;
`;

const StyledDivider = styled.div`
  align-items: center;
  background-color: #f2f2f3;
  color: #33414d;
  display: flex;
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 600;
  justify-content: flex-start;
  height: 24px;
  padding: 0px 0px 0px 16px;
  width: 100%;
`;

const StyledLazyItemsContainer = styled.div`
  flex: 1;
`;

const StyledContactListUL = styled.ul`
  background-color: ${colorGrid.gray0};
  display: flex;
  flex-direction: column;
  height: 100%;
  list-style-type: none;
  margin: 0px;
  padding: 0px;
`;

const AlphaSearchBar = styled.div`
  background: ${colorGrid.gray1};
  border-top: 1px solid ${colorGrid.gray3};
  display: block;
`;

const SearchTopContainer = styled.div`
  align-items: center;
  background-color: ${colorGrid.gray0};
  display: flex;
  justify-content: space-around;
`;

const SearchFilterContainer = styled.div`
  font-size: 14px;
  margin: 8px 10px;
  width: 100%;
`;

const AddRoundContactButton = styled(Button)`
  background-color: #fff;
  border: 1px solid #9c328c;
  display: flex;
  justify-content: center;
  height: 40px;
  margin-right: 10px;
  padding: 0 7px;
  width: 40px;

  :hover {
    background-color: #ebd6e8;
  }
`;

const LazyContainer = styled.div`
  align-items: center;
  align-self: center;
  background-color: ${colorGrid.gray1};
  box-shadow: inset 0 -1px 0 0 ${colorGrid.gray3}; // was #d9dde5
  display: flex;
  justify-content: flex-start;
  padding-bottom: 5px;
  padding-top: 5px;
`;

const LazyAvatarPlaceholder = styled.div`
  background-color: ${colorGrid.gray3}; // was #d9dde5
  border-radius: 50%;
  margin-left: 10px;
  margin-right: 10px;
  padding: 16px;
`;

const LazyNamePlaceholder = styled.div`
  background-color: ${colorGrid.gray3}; // was #d9dde5
  flex: 9;
  height: 20px;
`;

const ContactList = (props) => {
  const [contactList, , totalContacts, { getContacts }] = useContacts();
  let filterTimeout = 0;

  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [isRetrieving, setRetrieving] = useState(false);

  const mainGroupRef = useRef(null);

  const loadPage = async () => {
    await setRetrieving(true);
    await getContacts(searchText, limit, offset, props.intl);
    await setRetrieving(false);
  };
  // on initial render fetchContacts, and fetch again when searchText or offset change
  useEffect(() => {
    // console.log('Get Contacts effect', searchText, offset);
    loadPage();
  }, [searchText, offset]);

  // clearTimeouts on every update
  useEffect(() => {
    clearTimeout(filterTimeout);

    return function cleanup() {
      if (filterTimeout) {
        clearTimeout(filterTimeout);
      }
    };
  });

  // Setting a timeout to prevent calls to API on every single keypress
  const filterSearchInput = (input) => {
    if (filterTimeout !== 0) {
      clearTimeout(filterTimeout);
    }

    filterTimeout = setTimeout(() => {
      console.log("Call API to fetch filtered contacts", input);
      // getContacts(input);
      setSearchText(input);
      setOffset(0);
    }, 1000);
  };

  const handleScroll = () => {
    // on scroll, when we hit the bottom of the mainGroup container, check if we need to get more contacts
    // and update the limit and offset to get the next batch
    const containerRef = mainGroupRef.current;
    const atBottom = containerRef.scrollHeight - containerRef.scrollTop <= containerRef.offsetHeight;
    
    if (atBottom) { // get more if the offset and the current contactList length < total contacts on API for 'search'
      if (offset < totalContacts && contactList.length < totalContacts) { // get more!
        setLimit(20);
        setOffset(contactList.length);
      }
    }
  };

  const categorizeContactList = (rawContactList = []) => {
    // console.log('rawContactList', rawContactList);
    // This function builds the categorizedContactList object. This object allows us to build a sorted list with alphabetical dividers.
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890".split(""); // for contactList dividers
    const categorizedContactList = {};

    alphabet.forEach((letter) => {
      // Create an array of contacts whose first letter in name or email matches the current letter in forEach.
      const matchedContacts = rawContactList && rawContactList.filter((contact) => {
        if (contact.name.last || contact.name.first) {
          // concatenates last name and first name if they exist.
          const fullName = (contact.name.last ? contact.name.last : '') + (contact.name.first ? contact.name.first : '');

          return fullName.charAt(0).toUpperCase() === letter;
        } else if (contact.emails && contact.emails.length > 0) {
          return contact.emails[0].address.charAt(0).toUpperCase() === letter;
        } else {
          return false;
        }
      });

      // Assign key/values to categorizedContactList for any arrays that are not empty
      if (matchedContacts.length > 0) {
        categorizedContactList[letter] = matchedContacts;
      }
    });

    // Example return { "B" : [ { "name" : { "first" : "KC", "last" : "Foster"} } ] }
    return categorizedContactList;
  };

  const renderSearchFilter = (contactList, cbStringToFilter, cbAddOnClick, intl) => {
    return (
      <AlphaSearchBar className='alphaSearchBar' >
        <SearchTopContainer className='topContainer' >
          <SearchFilterContainer className='testingSearchExists'>
            <SearchFilter
              searchText={searchText}
              searchResultCount={contactList && contactList.length}
              cbStringToFilter={cbStringToFilter}
              placeholderText={contactList && Object.keys(contactList).length > 0 ? getIntlText(intl, 'ENTITYLIST-SEARCHBAR-SEARCH-CONTACTS', "Search contacts") : ""}
            />
          </SearchFilterContainer>
          <AddRoundContactButton
            ariaLabel={getIntlText(intl, 'ARIA-EVENT-LIST-ADD-NEW-GROUP', 'New Contact')}
            cbClick={cbAddOnClick}
            className='addContactButton'
            buttonShape="round"
            buttonSize="large"
            svgFill="#9c328c"
            svgType="AddIconSVG"
          />
        </SearchTopContainer>
      </AlphaSearchBar>
    );
  };

  const handleAddClick = () => {
    track("add_contact", "clicked");
    props.cbImportAddClick('add');
  };

  const handleImportClick = () => {
    track("import_contacts", "clicked");
    props.cbImportAddClick('import');
  };

  const renderZeroState = (cbImportClick, cbAddClick, intl) => {
    return (
      <ZeroStateContainer>
        <ZeroStateText>
          {
            getIntlText(intl, 'ENTITYLIST-NO-CONTACTS-FOUND', 'No contacts available. ' +
              'Import from your platform of choice by clicking import. Or add a new contact.')
          }
        </ZeroStateText>
        <ZeroStateButtonContainer className="importAddButtonArea">
          <ImportContactsButton
            buttonLabel={getIntlText(intl, 'ZEROSTATE-IMPORT-BTN-LABEL', "Import")}
            buttonSize="large"
            cbClick={cbImportClick}
          />
          <AddContactButton
            buttonLabel={getIntlText(intl, 'ZEROSTATE-ADD-CONTACT-BTN-LABEL', "Add Contact")}
            buttonSize="large"
            buttonType="secondary"
            cbClick={cbAddClick}
            fontColor="#243991"
          />
        </ZeroStateButtonContainer>
      </ZeroStateContainer>
    );
  };

  const renderRetrievingState = (intl) => {
    return (
      <RetrievingStateContainer>
        <ZeroStateTextImport>
          {getIntlText(intl, 'ENTITYLIST-RETRIEVING', "Busy retrieving contacts.")}
        </ZeroStateTextImport>
      </RetrievingStateContainer>
    );
  };

  // NOTE: style for placeholder are hard coded due to performance issues when
  // check/uncheck entity items when there is a large list.
  const renderLazyPlaceHolder = () => {
    return (
      <LazyContainer className="lazyContainer" >
        <LazyAvatarPlaceholder className="lazyAvatarPlaceholder" />
        <LazyNamePlaceholder className="lazyNamePlaceholder" />
      </LazyContainer>
    );
  };

  const renderContact = (contact, index, cbContactClick, cbContactActionClick, intl) => {
    let LazyContactListItem = (
      <LazyLoader
        placeholder={renderLazyPlaceHolder()}
        offset={320}
        throttle={100}
      >
        <ContactListItem
          actionTitle={'Send Message'}
          cbContactOnClick={cbContactClick}
          cbContactActionOnClick={cbContactActionClick}
          contact={contact}
          contactActionSVG={'MdPhone'}
          avatarBackgroundImage={'../assets/images/avatar-background.png'}
          intl={intl}
        />
      </LazyLoader>
    );

    return (
      <div
        className="groupLI"
        key={"i" + index}
      >
        {LazyContactListItem}
      </div>
    );
  };

  const renderListWithCategories = (categorizedContactList = {}, cbContactOnClick, cbContactActionOnClick, intl) => {
    return Object.keys(categorizedContactList).map((letter) => {
      const contactsList = categorizedContactList[letter].map((contact, index) => {
        return renderContact(contact, index, cbContactOnClick, cbContactActionOnClick, intl);
      });

      return (
        <StyledListItem
          className={'divider' + letter}
          key={'divider' + letter}
        >
          <StyledLiContent >
            <StyledDivider className="characterDivider" >
              {letter}
            </StyledDivider>
            <StyledLazyItemsContainer className="LazyItemsContainer" >
              {contactsList}
            </StyledLazyItemsContainer>
          </StyledLiContent>
        </StyledListItem>
      );
    });
  };

  const renderFullList = (categorizedContactList, cbContactOnClick, cbContactActionOnClick, intl) => {
    return (
      <StyledContactListUL className="createListContainer">
        {renderListWithCategories(categorizedContactList, cbContactOnClick, cbContactActionOnClick, intl)}
      </StyledContactListUL>
    );
  };

  const handleContactClick = (data) => {
    console.log("contact", "clicked");
    props.cbContactOnClick(data);
  };

  const listState = renderFullList(categorizeContactList(contactList), handleContactClick, props.cbContactActionOnClick, props.intl);
  const zeroState = renderZeroState(handleImportClick, handleAddClick, props.intl);
  const retrievingState = renderRetrievingState(props.intl);
  const screenView = isRetrieving && !contactList?.length ? retrievingState : (contactList && Object.keys(contactList).length > 0 ? listState : zeroState);

  return (
    <ContactGroupListContainer className='groupListArea'>
      {renderSearchFilter(contactList, filterSearchInput, props.cbAddOnClick, props.intl)}
      <MainGroupArea className='mainGroupArea'
        ref={mainGroupRef}
        onScroll={handleScroll}
      >
        {screenView}
      </MainGroupArea>
    </ContactGroupListContainer>
  );
};

ContactList.propTypes = {
  cbAddOnClick: PropTypes.func,
  cbContactOnClick: PropTypes.func,
  cbContactActionOnClick: PropTypes.func,
  cbImportAddClick: PropTypes.func,
  intl: PropTypes.object
};

ContactList.defaultProps = {
  cbAddOnClick: () => { console.log("cbAddOnClick fired"); },
  cbContactOnClick: () => { console.log("cbContactOnClick fired"); },
  cbContactActionOnClick: () => { console.log("cbContactActionOnClick fired"); },
  cbImportAddClick: () => { console.log("cbImportAddClick fired"); }
};

export default ContactList;
